import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import * as THREE from "three";
import { FLOOR_DIMENSIONS } from "../consts";

interface Textures {
	floor: THREE.Texture;
	wall: THREE.Texture;
	ceiling: THREE.Texture;
}

const TextureContext = createContext<Textures | null>(null);

export const useTextures = () => {
	const ctx = useContext(TextureContext);
	if (!ctx) throw new Error("useTextures must be used inside TextureProvider");
	return ctx;
};

export const TextureProvider = ({ children, onLoaded }: { children: ReactNode; onLoaded?: () => void }) => {
	const [textures, setTextures] = useState<Textures | null>(null);

	useEffect(() => {
		const loader = new THREE.TextureLoader();
		Promise.all([
			loader.loadAsync("/tran/san0.png"),
			loader.loadAsync("/textures/wall.jpeg"),
			loader.loadAsync("/tran/tran.png"),
		]).then(([floor, wall, ceiling]) => {
			floor.wrapS = floor.wrapT = THREE.RepeatWrapping;

			const TILE_WIDTH = 2;
			const IMG_W = 1;
			const IMG_H = 1;
			const TILE_HEIGHT = TILE_WIDTH * (IMG_H / IMG_W);
			floor.repeat.set(FLOOR_DIMENSIONS[0] / TILE_WIDTH, FLOOR_DIMENSIONS[1] / TILE_HEIGHT);

			wall.anisotropy = 4;
			wall.wrapS = wall.wrapT = THREE.RepeatWrapping;
			wall.repeat.set(5, 1);

			ceiling.wrapS = ceiling.wrapT = THREE.RepeatWrapping;
			ceiling.repeat.set(10, 10);

			setTextures({ floor, wall, ceiling });
			onLoaded?.();
		}).catch((err) => {
			// Without this, a single failed texture (e.g. a 404 on a case-sensitive
			// server) rejects the Promise.all silently and the app hangs here forever.
			console.error("[virtual-eshop] Texture load failed:", err);
		});
	}, []);

	if (!textures) return null;

	return (
		<TextureContext.Provider value={textures}>
			{children}
		</TextureContext.Provider>
	);
};
